import React, { useEffect, useMemo, useRef, useState } from 'react';
import GenericDropdown from '../GenericDropdown';
import ApiSourceRequestTabs from '../ApiSourceRequestTabs';
import {
  createEditorRow,
  editorRowsFromParamDefs,
  normalizeBodyParamDefs,
  normalizeHeaderParamDefs,
  normalizeParamDefs,
  paramDefsFromEditorRows,
  resolveHttpMethod,
} from '../../utils/apiQueryParams';

function initialAuthToken(apiSource = {}) {
  if (apiSource.api_key_value) return apiSource.api_key_value;
  if (apiSource.auth_type === 'basic' && apiSource.auth_user) {
    return `${apiSource.auth_user}:${apiSource.auth_pass || ''}`;
  }
  return apiSource.auth_token || '';
}

function rowsOrEmpty(defs) {
  const rows = editorRowsFromParamDefs(defs);
  return rows.length ? rows : [createEditorRow()];
}

const ApiSourceConfig = ({ value = {}, onChange, latestConfigRef = null }) => {
  const apiSource = value.api_source || {};
  const inferredProvider = apiSource.provider || (apiSource.endpoint ? 'custom' : 'xe.net');
  const fieldMapping = apiSource.field_mapping;

  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  useEffect(() => {
    onChangeRef.current = onChange;
    valueRef.current = value;
  });

  const [provider, setProvider] = useState(inferredProvider);
  const [baseCurrency, setBaseCurrency] = useState(apiSource.base_currency || 'USD');
  const [currencies, setCurrencies] = useState(
    Array.isArray(apiSource.currencies) ? apiSource.currencies.join(', ') : 'EUR, GBP, JPY, CAD, AUD, CHF, CNY, INR, MXN, BRL'
  );
  const [endpoint, setEndpoint] = useState(apiSource.endpoint || '');
  const [httpMethod, setHttpMethod] = useState(resolveHttpMethod(apiSource));
  const [authType, setAuthType] = useState(apiSource.auth_type || 'none');
  const [authToken, setAuthToken] = useState(() => initialAuthToken(apiSource));
  const [queryParamRows, setQueryParamRows] = useState(() => rowsOrEmpty(normalizeParamDefs(apiSource)));
  const [headerParamRows, setHeaderParamRows] = useState(() => rowsOrEmpty(normalizeHeaderParamDefs(apiSource)));
  const [bodyParamRows, setBodyParamRows] = useState(() => rowsOrEmpty(normalizeBodyParamDefs(apiSource)));
  const [autoGenerateColumns, setAutoGenerateColumns] = useState(
    Boolean(apiSource.auto_generate_columns_from_json)
  );
  const [dataPath, setDataPath] = useState(apiSource.data_path || '');

  const providerOptions = [
    { id: 'xe.net', name: 'XE.net (Currency Rates)' },
    { id: 'custom', name: 'Custom API' },
  ];

  const httpMethodOptions = [
    { id: 'GET', name: 'GET — query string' },
    { id: 'POST', name: 'POST — JSON body' },
  ];

  const previewApiSource = useMemo(
    () => ({
      endpoint,
      http_method: httpMethod,
      auth_type: authType,
      auth_token: authToken,
      query_param_defs: paramDefsFromEditorRows(queryParamRows),
      header_param_defs: paramDefsFromEditorRows(headerParamRows),
      body_param_defs: paramDefsFromEditorRows(bodyParamRows),
    }),
    [endpoint, httpMethod, authType, authToken, queryParamRows, headerParamRows, bodyParamRows]
  );

  useEffect(() => {
    const apiSourceConfig = { provider };

    if (provider === 'xe.net') {
      apiSourceConfig.base_currency = baseCurrency || 'USD';
      apiSourceConfig.currencies = (currencies || '').split(',').map((c) => c.trim()).filter(Boolean);
    } else if (provider === 'custom') {
      apiSourceConfig.endpoint = endpoint;
      apiSourceConfig.http_method = httpMethod;
      apiSourceConfig.auth_type = authType;

      if (authType === 'bearer' && authToken) {
        apiSourceConfig.auth_token = authToken;
      } else if (authType === 'basic' && authToken) {
        const idx = authToken.indexOf(':');
        if (idx >= 0) {
          apiSourceConfig.auth_user = authToken.slice(0, idx);
          apiSourceConfig.auth_pass = authToken.slice(idx + 1);
        } else {
          apiSourceConfig.auth_token = authToken;
        }
      } else if (authType === 'api_key' && authToken) {
        apiSourceConfig.api_key_header = 'X-Api-Key';
        apiSourceConfig.api_key_value = authToken;
      }

      const queryDefs = paramDefsFromEditorRows(queryParamRows);
      apiSourceConfig.query_param_defs = queryDefs;
      const params = {};
      for (const row of queryDefs) {
        if (row.default !== '') params[row.key] = row.default;
      }
      if (Object.keys(params).length) apiSourceConfig.params = params;

      const headerDefs = paramDefsFromEditorRows(headerParamRows);
      apiSourceConfig.header_param_defs = headerDefs;
      const headers = {};
      for (const row of headerDefs) {
        if (row.default !== '') headers[row.key] = row.default;
      }
      if (Object.keys(headers).length) apiSourceConfig.headers = headers;

      if (httpMethod === 'POST') {
        const bodyDefs = paramDefsFromEditorRows(bodyParamRows);
        apiSourceConfig.body_param_defs = bodyDefs;
        const body = {};
        for (const row of bodyDefs) {
          if (row.default !== '') body[row.key] = row.default;
        }
        if (Object.keys(body).length) apiSourceConfig.body = body;
      }

      const trimmedDataPath = (dataPath || '').trim();
      if (trimmedDataPath) apiSourceConfig.data_path = trimmedDataPath;
      if (fieldMapping) apiSourceConfig.field_mapping = fieldMapping;
    }

    if (autoGenerateColumns) {
      apiSourceConfig.auto_generate_columns_from_json = true;
    }

    const prev = valueRef.current || {};
    const nextConfig = {
      ...prev,
      type: 'api_source',
      api_source: apiSourceConfig,
      // Preserve group-level dedupe when API panel re-emits
      ...(prev.dedupe ? { dedupe: prev.dedupe } : {}),
    };
    // Drop legacy per-source mode / unique keys from emitted config
    delete nextConfig.mode;

    if (latestConfigRef) {
      latestConfigRef.current = nextConfig;
    }

    const timer = window.setTimeout(() => {
      onChangeRef.current?.(nextConfig);
    }, 200);

    return () => window.clearTimeout(timer);
  }, [
    provider,
    baseCurrency,
    currencies,
    endpoint,
    httpMethod,
    authType,
    authToken,
    queryParamRows,
    headerParamRows,
    bodyParamRows,
    autoGenerateColumns,
    dataPath,
    fieldMapping,
    latestConfigRef,
  ]);

  // Keep group-level dedupe on the save ref when Deduplication changes without touching API fields
  useEffect(() => {
    if (!latestConfigRef) return;
    const prev = latestConfigRef.current;
    if (!prev || typeof prev !== 'object') return;
    const nextDedupe = value?.dedupe;
    if (nextDedupe === undefined) return;
    latestConfigRef.current = { ...prev, dedupe: nextDedupe };
  }, [value?.dedupe, latestConfigRef]);

  return (
    <div className="api-source-config">
      <div className="form-row">
        <label>API Provider <span className="required">*</span></label>
        <GenericDropdown
          items={providerOptions}
          value={provider}
          onChange={(id) => setProvider(id || 'xe.net')}
          placeholder="Select provider"
        />
      </div>

      <div className="form-row">
        <label>
          <input
            type="checkbox"
            checked={autoGenerateColumns}
            onChange={(e) => setAutoGenerateColumns(e.target.checked)}
          />
          {' '}Generate columns from JSON keys on ingestion
        </label>
        <div className="hint">
          On each ingest, scan all fetched items once, collect unique JSON keys (including nested fields),
          and create matching columns. Use <strong>Prompt data path</strong> below when the API wraps rows
          in a nested field or array.
        </div>
      </div>

      {provider === 'xe.net' && (
        <>
          <div className="form-row">
            <div className="hint" style={{
              padding: '12px',
              background: 'rgba(33, 150, 243, 0.1)',
              borderRadius: '6px',
              border: '1px solid rgba(33, 150, 243, 0.3)',
              marginBottom: '4px',
            }}
            >
              <i className="fas fa-info-circle" /> XE.net credentials (Account ID &amp; API Key) must be configured in{' '}
              <strong>System Settings</strong> as <code>XE_NET_ACCOUNT_ID</code> and <code>XE_NET_API_KEY</code>.
              Provider configuration is stored in <code>API_PROVIDER_BLOB</code>.
            </div>
          </div>
          <div className="form-row">
            <label>Base Currency</label>
            <input
              type="text"
              value={baseCurrency}
              onChange={(e) => setBaseCurrency(e.target.value.toUpperCase())}
              placeholder="USD"
              maxLength={3}
              style={{ width: '100px' }}
            />
            <div className="hint">3-letter ISO currency code (e.g., USD, EUR, GBP)</div>
          </div>
          <div className="form-row">
            <label>Target Currencies</label>
            <input
              type="text"
              value={currencies}
              onChange={(e) => setCurrencies(e.target.value.toUpperCase())}
              placeholder="EUR, GBP, JPY, CAD"
            />
            <div className="hint">Comma-separated list of target currency codes</div>
          </div>
        </>
      )}

      {provider === 'custom' && (
        <>
          <div className="form-row">
            <label>API Endpoint <span className="required">*</span></label>
            <input
              type="text"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder="https://api.example.com/v1/resource"
            />
            <div className="hint">Base URL without query string.</div>
          </div>
          <div className="form-row">
            <label>HTTP Method <span className="required">*</span></label>
            <div className="http-method-options" role="radiogroup" aria-label="HTTP method">
              {httpMethodOptions.map((option) => (
                <label
                  key={option.id}
                  className={`http-method-option${httpMethod === option.id ? ' is-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="api-http-method"
                    value={option.id}
                    checked={httpMethod === option.id}
                    onChange={() => setHttpMethod(option.id)}
                  />
                  {option.name}
                </label>
              ))}
            </div>
          </div>
          <div className="form-row">
            <label>Prompt data path</label>
            <input
              type="text"
              value={dataPath}
              onChange={(e) => setDataPath(e.target.value)}
              placeholder="(blank = entire JSON response)"
            />
            <div className="hint">
              Where to find rows for ingestion. Examples: <code>items</code>, <code>data.results</code>.
            </div>
          </div>
          <div className="form-row">
            <label>Request configuration</label>
            <ApiSourceRequestTabs
              httpMethod={httpMethod}
              endpoint={endpoint}
              previewApiSource={previewApiSource}
              queryParamRows={queryParamRows}
              onQueryParamRowsChange={setQueryParamRows}
              headerParamRows={headerParamRows}
              onHeaderParamRowsChange={setHeaderParamRows}
              bodyParamRows={bodyParamRows}
              onBodyParamRowsChange={setBodyParamRows}
              authType={authType}
              onAuthTypeChange={setAuthType}
              authToken={authToken}
              onAuthTokenChange={setAuthToken}
            />
          </div>
          <div className="form-row">
            <div className="hint" style={{
              padding: '12px',
              background: 'rgba(255, 152, 0, 0.1)',
              borderRadius: '6px',
              border: '1px solid rgba(255, 152, 0, 0.3)',
            }}
            >
              <i className="fas fa-exclamation-triangle" /> Custom API responses must return JSON.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ApiSourceConfig;
